import { RootErrorComponent } from "@/components/error/root-error";
import { SpinnerPage } from "@/components/loading/spinner.page";
import { AuthenticatedHeader, InviteHeader } from "@/components/navigation/authenticated-header";
import { PublicHeader } from "@/components/navigation/public-header";
import { SidebarProvider } from "@/components/ui/sidebar";
import { clearPostAuthRedirect } from "@/features/auth/post-auth-redirect";
import { notionSearchQueryRoot } from "@/features/notion/hooks/use-notion-page-search";
import { projectAccessQueries } from "@/features/projects/api/project-access-keys";
import { incognitoQueries } from "@/features/superuser/incognito/api/incognito-keys";
import { superuserWorkspacesQueries } from "@/features/superuser/workspaces/api/workspaces-keys";
import { clearPendingInvite, getPendingInvite } from "@/features/workspace-invites/invite-storage";
import { LOCALE_KEY } from "@/lib/local-storage.consts";
import { supabase } from "@/lib/supabase";
import * as Sentry from "@sentry/react";
import type { Session } from "@supabase/supabase-js";
import type { QueryClient } from "@tanstack/react-query";
import {
  Outlet,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
} from "@tanstack/react-router";
import { NuqsAdapter } from "nuqs/adapters/tanstack-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocalStorage } from "usehooks-ts";
import { loadLocale } from "wuchale/load-utils";
// import { TanStackRouterDevtools } from '@tanstack/router-devtools'

const { logger } = Sentry;

export interface RouterContext {
  queryClient: QueryClient;
}

const RootLayout = () => {
  const router = useRouter();
  const [authReady, setAuthReady] = useState(false);
  const [session, setSession] = useState<Session | null>(null);
  const [locale] = useLocalStorage<string>(LOCALE_KEY, "en");
  const [localeLoaded, setLocaleLoaded] = useState(false);
  const localeInitializedRef = useRef(false);

  const { isAuthenticated, location } = useRouterState({
    select: (s) => ({
      isAuthenticated: s.matches.some((m) => m.routeId === "/_authenticated"),
      location: s.location,
    }),
  });

  const lastHandledInviteCodeRef = useRef<string | null>(null);

  const maybeRedirectToInvite = useCallback(
    (nextSession: Session | null): boolean => {
      if (!nextSession) {
        lastHandledInviteCodeRef.current = null;
        return false;
      }

      const metadata = nextSession.user.user_metadata as {
        invite_code?: unknown;
        invite_type?: unknown;
      } | null;

      let inviteCode: string | null = null;
      let inviteType: string | null = null;

      if (metadata) {
        const inviteCodeRaw = metadata.invite_code;
        inviteCode =
          typeof inviteCodeRaw === "string" && inviteCodeRaw.trim().length > 0
            ? inviteCodeRaw
            : null;

        const inviteTypeRaw = metadata.invite_type;
        inviteType = typeof inviteTypeRaw === "string" ? inviteTypeRaw.toLowerCase() : null;
      }

      if (!inviteCode || !inviteType) {
        const pendingInvite = getPendingInvite();

        if (pendingInvite) {
          inviteCode = inviteCode ?? pendingInvite.code;
          inviteType = pendingInvite.type ? pendingInvite.type.toLowerCase() : null;
        }
      }

      if (!inviteCode) {
        lastHandledInviteCodeRef.current = null;
        return false;
      }

      if (inviteType !== "workspace" && inviteType !== "project") {
        lastHandledInviteCodeRef.current = null;
        return false;
      }

      if (lastHandledInviteCodeRef.current === inviteCode) {
        return false;
      }

      lastHandledInviteCodeRef.current = inviteCode;
      clearPendingInvite();

      const invitePath = `/invite/${inviteCode}`;

      if (location.pathname === invitePath) {
        return true;
      }

      void router.navigate({
        to: "/invite/$code",
        params: { code: inviteCode },
      });

      return true;
    },
    [location.pathname, router],
  );

  useEffect(() => {
    maybeRedirectToInvite(session);
  }, [maybeRedirectToInvite, session]);

  // Resolve the initial session from storage before unblocking the UI.
  // This is the single source of truth for "has the app bootstrapped auth".
  // Supabase caches the result in memory so every subsequent getSession()
  // call (e.g. in beforeLoad guards) is effectively a synchronous read.
  useEffect(() => {
    async function resolveInitialSession() {
      try {
        const {
          data: { session: initialSession },
        } = await supabase.auth.getSession();
        setSession(initialSession);
      } catch (error) {
        logger.error("Auth: failed to resolve initial session");
        Sentry.captureException(error);
        // Unblock the UI even on failure so the user is not stuck on a spinner
      } finally {
        setAuthReady(true);
      }
    }

    void resolveInitialSession();
  }, []);

  // Keep all browser tabs in sync.
  //
  // Supabase propagates auth events across tabs via BroadcastChannel (modern
  // browsers) or storage events (fallback), so onAuthStateChange fires here
  // whenever another tab signs in or out.
  //
  // Calling router.invalidate() re-runs every active beforeLoad guard:
  //   • SIGNED_OUT  → _authenticated guard finds no session → redirect /login
  //   • SIGNED_IN   → requireGuest on /login finds a session → redirect /workspaces
  //
  // TOKEN_REFRESHED and INITIAL_SESSION are skipped — the session is still
  // valid and the guards don't need to re-run.
  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, nextSession) => {
      setSession(nextSession);
      const redirected = maybeRedirectToInvite(nextSession);

      if (["INITIAL_SESSION", "TOKEN_REFRESHED"].includes(event as string)) {
        return;
      }

      if (redirected) {
        return;
      }

      // CR-116: privileged superuser reads must not outlive the session that
      // made them (shared tab, account switch).
      if (event === "SIGNED_OUT") {
        const { queryClient } = router.options.context;
        queryClient.removeQueries({ queryKey: incognitoQueries.root() });
        queryClient.removeQueries({ queryKey: superuserWorkspacesQueries.root() });
        // CR-203: owners' join-request lists carry requesters' names/emails.
        queryClient.removeQueries({ queryKey: projectAccessQueries.all() });
        // CR-230: Notion search results are titles from a project's connection.
        queryClient.removeQueries({ queryKey: notionSearchQueryRoot() });

        // CR-288: a sign-out lands on a plain /login. Letting the
        // _authenticated guard re-run would carry the page just left as a
        // return-to, and the next account to sign in on this browser would be
        // sent to the previous user's page.
        clearPostAuthRedirect();
        if (router.state.matches.some((match) => match.routeId === "/_authenticated")) {
          logger.debug("Auth: onAuthStateChange", { event });
          void router.navigate({ to: "/login", replace: true });
          return;
        }
      }

      logger.debug("Auth: onAuthStateChange", { event });
      void router.invalidate();
    });

    return () => subscription.unsubscribe();
  }, [maybeRedirectToInvite, router]);

  // Load locale — only block rendering on the very first load to avoid
  // unmounting the component tree on every language change mid-session.
  useEffect(() => {
    async function loadLanguage() {
      if (!localeInitializedRef.current) {
        setLocaleLoaded(false);
      }
      try {
        await loadLocale(locale);
        localeInitializedRef.current = true;
        setLocaleLoaded(true);
      } catch (error) {
        console.error("Failed to load locale:", error);
        localeInitializedRef.current = true;
        setLocaleLoaded(true);
      }
    }
    void loadLanguage();
  }, [locale]);

  // Block rendering until both the initial auth session and the locale are
  // ready — prevents a flash of the wrong page or untranslated content.
  if (!authReady || !localeLoaded) {
    return <SpinnerPage />;
  }

  const Header = isAuthenticated ? (
    <AuthenticatedHeader />
  ) : session ? (
    <InviteHeader user={session.user} />
  ) : (
    <PublicHeader />
  );

  return (
    <NuqsAdapter>
      <SidebarProvider>
        <div className="min-h-screen min-w-screen bg-background">
          <div className="sticky top-0 z-50">{Header}</div>
          <main>
            <Outlet />
          </main>
        </div>
      </SidebarProvider>
    </NuqsAdapter>
  );
};

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  errorComponent: RootErrorComponent,
});
